import { useState } from 'react';
import { Boxes, ChevronRight, Search } from 'lucide-react';
import { Badge, Input } from '@eombor/ui';
import type { SubdivisionReportRow } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { useSubdivisionReport } from '../model/report.queries.js';
import { ReportCard } from './ReportCard.js';
import { SubdivisionStockDialog } from './SubdivisionStockDialog.js';

/**
 * Bo'linmalar va ularning kichik omborlari.
 *
 * Qidiruv SERVERDA (`?q=`), mahalliy filtr emas: bir MTU da yuzlab bo'linma
 * bor va ularning hammasini brauzerga tortib kelish tanlagichdagi xatoning
 * aynan o'zi bo'lardi.
 *
 * Satr bosilganda modal ochiladi — alohida sahifa emas: rahbar "shu bo'limda
 * nima bor?" degan savolga javob olib, DARHOL ro'yxatga qaytadi.
 */
export const SubdivisionsBlock = () => {
  const [q, setQ] = useState('');
  const [opened, setOpened] = useState<SubdivisionReportRow | null>(null);
  const query = useSubdivisionReport(q, 1);

  const rows = query.data?.items ?? [];

  return (
    <>
      <ReportCard
        icon={Boxes}
        tone="accent"
        title={t('report.subdivisionsTitle')}
        hint={t('report.subdivisionsHint')}
        pending={query.isPending}
        error={query.error ?? null}
        empty={rows.length === 0}
        emptyText={q === '' ? t('report.subdivisionsEmpty') : t('report.subdivisionsNotFound')}
        actions={
          <Input
            variant="filter"
            type="search"
            aria-label={t('report.subdivisionsSearch')}
            placeholder={t('report.subdivisionsSearch')}
            prefix={<Search size={16} />}
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
        }
      >
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setOpened(row)}
                className="group flex w-full min-w-0 items-center gap-3 rounded-[var(--radius-md)] border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-surface-2 focus-visible:border-border focus-visible:bg-surface-2"
              >
                {/* Kod belgichada — «R va AX» takrorlanadi, kod esa
                    korxona ichida yagona va ko'z uni tez ilg'aydi. */}
                <span
                  aria-hidden
                  className="tabular flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent-soft text-[11px] font-semibold text-accent"
                >
                  {row.code.slice(0, 4)}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-text">{row.name}</span>
                  {/* Egasi HAR DOIM birga: «R va AX» bir necha korxonada bor
                      va yolg'iz nom qaysi biri ekanini aytmaydi. */}
                  <span className="block truncate text-xs text-text-faint">{row.orgUnitName}</span>
                </span>

                {/* Bo'sh bo'linma so'nik turadi: nol ham javob, lekin u
                    e'tibor talab qilmaydi. */}
                <Badge tone={row.itemCount > 0 ? 'accent' : 'neutral'} mono>
                  {t('report.subdivisionsItems', { count: row.itemCount })}
                </Badge>
                <ChevronRight
                  size={16}
                  aria-hidden
                  className="shrink-0 text-text-faint opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                />
              </button>
            </li>
          ))}
        </ul>
      </ReportCard>

      <SubdivisionStockDialog subdivision={opened} onClose={() => setOpened(null)} />
    </>
  );
};
